/**
 * Formulario de "Novo Monitor" — portado do protótipo (`MonitorForm.tsx`), com os
 * campos que a tabela `monitores` de fato tem (db/migrations/0002).
 *
 * O QUE FICOU DE FORA, DE PROPÓSITO:
 * - Busca automática de CPF: o protótipo chamava uma função mock de Receita
 *   Federal (`mock-serpro`). Simular dado oficial de verdade é exatamente o que
 *   CLAUDE.md, "Proibido", recusa — o campo aqui é só digitado, com máscara e
 *   validação de dígito verificador.
 * - `DocumentChecklist`: pertence a um módulo de documentos/OCR que não existe
 *   neste repositório.
 * - `CursosObrigatoriosManager`: pressupõe uma tabela normalizada de cursos que
 *   não existe aqui — os cursos de monitor são colunas fixas (booleano +
 *   validade) na própria tabela `monitores`, e por isso viram Switch + campo de
 *   data direto no formulário.
 * - Select dinâmico de natureza do vínculo (`useParametroOptions`): não existe
 *   API de parâmetros neste sistema. `tipo_vinculo_empregatorio` é um `Input` de
 *   texto simples.
 * - Telefone, e-mail e endereço: minimização de dados (LGPD, decisão já tomada
 *   no protótipo). Esses dados de contato de Monitor vivem no SGTP e no
 *   cadastro do Credenciado — não neste formulário. O aviso abaixo explica isso
 *   a quem está preenchendo.
 *
 * O "Salvar" NÃO CHAMA API NENHUMA — nenhuma tabela cadastral aceita escrita
 * hoje (docs/ALVOS.md, Onda 2; ver `comum/cadastroIndisponivel.ts`). Ele só
 * mostra o aviso e mantém os dados no formulário.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { GraduationCap, Link2, MessageSquare, ShieldCheck, UserCircle } from 'lucide-react';
import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { CredenciadoCombobox } from '@/modulos/operacao/comum/CredenciadoCombobox';
import {
  AVISO_DE_CADASTRO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { OPCOES_DE_SITUACAO_DA_PESSOA } from '@/modulos/operacao/comum/contrato';
import { mascaraCpf, somenteDigitos } from '@/nucleo/formato/documentos';
import {
  carregarRascunho,
  limparRascunho,
  useRascunhoDeFormulario,
} from '@/nucleo/ganchos/useRascunhoDeFormulario';
import { validarCpf } from '@/nucleo/validarDocumento';
import { AvisoDeRascunho } from '@/ui/AvisoDeRascunho';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/ui/base/form';
import { Input } from '@/ui/base/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Switch } from '@/ui/base/switch';
import { Textarea } from '@/ui/base/textarea';
import { FormSection } from '@/ui/FormSection';
import { RodapeDoFormularioEmModal } from '@/ui/forms/RodapeDoFormularioEmModal';

const CHAVE_DO_RASCUNHO = 'monitor-form';

const esquema = z
  .object({
    cpf: z.string().min(1, 'CPF obrigatório'),
    nome: z.string().trim().min(2, 'Nome deve ter pelo menos 2 caracteres').max(200),
    rg: z.string().max(20).optional(),
    data_nascimento: z.string().optional(),
    status: z.enum(['ATIVO', 'INATIVO', 'SUSPENSO']),
    curso_primeiro_socorros: z.boolean(),
    curso_primeiro_socorros_validade: z.string().optional(),
    curso_transporte_escolar: z.boolean(),
    curso_transporte_escolar_validade: z.string().optional(),
    data_vencimento_atestado_medico: z.string().optional(),
    certidao_criminal_emissao: z.string().optional(),
    credenciado_id: z.string().nullable(),
    tipo_vinculo_empregatorio: z.string().max(100).optional(),
    observacoes: z.string().max(1000).optional(),
  })
  .superRefine((valores, ctx) => {
    if (!validarCpf(valores.cpf)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cpf'],
        message: 'CPF inválido',
      });
    }
  });

export type ValoresDoFormulario = z.infer<typeof esquema>;

const VALORES_INICIAIS: ValoresDoFormulario = {
  cpf: '',
  nome: '',
  rg: '',
  data_nascimento: '',
  status: 'ATIVO',
  curso_primeiro_socorros: false,
  curso_primeiro_socorros_validade: '',
  curso_transporte_escolar: false,
  curso_transporte_escolar_validade: '',
  data_vencimento_atestado_medico: '',
  certidao_criminal_emissao: '',
  credenciado_id: null,
  tipo_vinculo_empregatorio: '',
  observacoes: '',
};

export interface MonitorFormularioProps {
  onCancelar: () => void;
  /**
   * Pré-preenche o formulário com os dados de um monitor existente — usado
   * pela tela de edição (`MonitorEditarPage`). Sem isto, o formulário abre em
   * branco (caso de "Novo Monitor").
   */
  valoresIniciais?: Partial<ValoresDoFormulario>;
  /**
   * `true` quando este formulário roda numa PÁGINA inteira (`MonitorEditarPage`),
   * sem `ResponsiveModal` por perto para prover o rodapé fixo — sem isto o rodapé
   * (Cancelar/Salvar) não apareceria. Default `false`: mantém o comportamento de
   * "Novo Monitor" (dentro de `ResponsiveModal`) inalterado.
   */
  rodapeInline?: boolean;
}

export function MonitorFormulario({
  onCancelar,
  valoresIniciais,
  rodapeInline,
}: MonitorFormularioProps) {
  const idBase = useId();
  const formId = `monitor-formulario-${idBase.replace(/:/g, '')}`;

  const [mostrarAvisoDeRascunho, setMostrarAvisoDeRascunho] = useState(
    () => carregarRascunho<ValoresDoFormulario>(CHAVE_DO_RASCUNHO) !== null,
  );

  const form = useForm<ValoresDoFormulario>({
    resolver: zodResolver(esquema),
    defaultValues: { ...VALORES_INICIAIS, ...valoresIniciais },
    mode: 'onSubmit',
  });

  const valores = form.watch();
  const gravadoEm = useRascunhoDeFormulario(CHAVE_DO_RASCUNHO, valores, true);

  function restaurarRascunho() {
    const rascunho = carregarRascunho<ValoresDoFormulario>(CHAVE_DO_RASCUNHO);
    if (rascunho) {
      form.reset(rascunho);
    }
    setMostrarAvisoDeRascunho(false);
  }

  function descartarRascunho() {
    limparRascunho(CHAVE_DO_RASCUNHO);
    setMostrarAvisoDeRascunho(false);
  }

  function enviar() {
    mostrarAviso(AVISO_DE_CADASTRO_INDISPONIVEL);
  }

  return (
    <Form {...form}>
      <form id={formId} onSubmit={form.handleSubmit(enviar)} className="space-y-6" noValidate>
        {mostrarAvisoDeRascunho && (
          <AvisoDeRascunho
            gravadoEm={gravadoEm}
            aoRestaurar={restaurarRascunho}
            aoDescartar={descartarRascunho}
          />
        )}

        <Alert>
          <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            Minimização de dados (LGPD). Contato e endereço de Monitor não são armazenados aqui —
            esses dados vivem no SGTP e no cadastro do Credenciado.
          </AlertDescription>
        </Alert>

        <FormSection title="Dados Pessoais" icon={UserCircle}>
          <div className="grid gap-5 sm:grid-cols-3">
            <FormField
              control={form.control}
              name="cpf"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CPF</FormLabel>
                  <FormControl>
                    <Input
                      value={mascaraCpf(field.value)}
                      onChange={(e) => field.onChange(somenteDigitos(e.target.value))}
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={14}
                      placeholder="000.000.000-00"
                      className="font-mono"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nome"
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input {...field} maxLength={200} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <FormField
              control={form.control}
              name="rg"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>RG</FormLabel>
                  <FormControl>
                    <Input {...field} maxLength={20} autoComplete="off" />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="data_nascimento"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Data de nascimento</FormLabel>
                  <FormControl>
                    <Input {...field} type="date" />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Situação</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {OPCOES_DE_SITUACAO_DA_PESSOA.map((opcao) => (
                        <SelectItem key={opcao.value} value={opcao.value}>
                          {opcao.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection
          title="Cursos Obrigatórios"
          subtitle="Capacitações e validades exigidas do monitor"
          icon={GraduationCap}
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="curso_primeiro_socorros"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border border-border/60 p-3">
                  <FormLabel className="cursor-pointer">Primeiros socorros</FormLabel>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="curso_primeiro_socorros_validade"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Validade — Primeiros socorros</FormLabel>
                  <FormControl>
                    <Input {...field} type="date" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="curso_transporte_escolar"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border border-border/60 p-3">
                  <FormLabel className="cursor-pointer">Transporte escolar</FormLabel>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="curso_transporte_escolar_validade"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Validade — Transporte escolar</FormLabel>
                  <FormControl>
                    <Input {...field} type="date" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="data_vencimento_atestado_medico"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vencimento do atestado médico</FormLabel>
                  <FormControl>
                    <Input {...field} type="date" />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="certidao_criminal_emissao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Emissão da certidão criminal</FormLabel>
                  <FormControl>
                    <Input {...field} type="date" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection title="Vínculo" icon={Link2}>
          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="credenciado_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Credenciado</FormLabel>
                  <CredenciadoCombobox
                    value={field.value}
                    onChange={field.onChange}
                    ariaLabel="Credenciado"
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="tipo_vinculo_empregatorio"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vínculo empregatício</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      maxLength={100}
                      autoComplete="off"
                      placeholder="Ex: CLT, autônomo…"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection title="Observações" icon={MessageSquare}>
          <FormField
            control={form.control}
            name="observacoes"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    {...field}
                    maxLength={1000}
                    rows={3}
                    placeholder="Informações adicionais sobre o monitor…"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </FormSection>

        <RodapeDoFormularioEmModal formId={formId} aoCancelar={onCancelar} inline={rodapeInline} />
      </form>
    </Form>
  );
}

export default MonitorFormulario;
